import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { getReadableTextColour } from '@payaca/utilities/colourUtilities';
import { FunctionComponent, useMemo } from 'react';
import {
  avatarTextVariants,
  avatarVariants,
  TAvatarSizeVariant,
  TAvatarStyleVariant,
} from './avatarVariants';

export enum AvatarSizeVariant {
  XXS = 'xxs',
  XS = 'xs',
  SM = 'sm',
  BASE = 'md',
  LG = 'lg',
  XL = 'xl',
}

export enum AvatarBorderColourVariant {
  WHITE = 'white',
  BLUE = 'blue',
}

export type AvatarProps = {
  sizeVariant?: TAvatarSizeVariant;
  styleVariant?: TAvatarStyleVariant;
  borderColourVariant?: 'white' | 'blue' | 'none';
  imgSrc?: string | null;
  imgDescription?: string;
  placeholderInitials?: string | null;
  iconName?: TIconName | null;
  colour?: string | null;
};

const Avatar: FunctionComponent<AvatarProps> = ({
  sizeVariant = 'md',
  styleVariant = 'circle',
  borderColourVariant = 'white',
  imgSrc,
  imgDescription,
  placeholderInitials,
  colour,
  iconName,
}) => {
  const readableTextColour = useMemo(() => {
    if (!colour) return undefined;

    return getReadableTextColour(colour);
  }, [colour]);

  if (imgSrc)
    return (
      <img
        className={avatarVariants({
          size: sizeVariant,
          shape: styleVariant,
          border: borderColourVariant,
        })}
        src={imgSrc}
        alt={imgDescription}
      />
    );

  if (iconName) {
    return (
      <span
        style={{
          backgroundColor: colour || undefined,
          color: readableTextColour,
        }}
        className={avatarVariants({
          size: sizeVariant,
          shape: styleVariant,
          border: borderColourVariant,
          className:
            'inline-flex items-center justify-center bg-gray-600 text-white',
        })}
      >
        <UntitledIcon className="h-3/5 w-3/5" name={iconName} />
      </span>
    );
  }

  if (placeholderInitials) {
    return (
      <span
        style={{ backgroundColor: colour || undefined }}
        className={avatarVariants({
          size: sizeVariant,
          shape: styleVariant,
          border: borderColourVariant,
          className:
            'shrink-0 inline-flex items-center justify-center bg-gray-600',
        })}
      >
        <span
          style={{ color: readableTextColour }}
          className={avatarTextVariants({ size: sizeVariant })}
        >
          {placeholderInitials}
        </span>
      </span>
    );
  }

  return (
    <span
      style={{
        backgroundColor: colour || undefined,
        color: readableTextColour,
      }}
      className={avatarVariants({
        size: sizeVariant,
        shape: styleVariant,
        border: borderColourVariant,
        className: 'bg-gray-100 text-gray-300 overflow-hidden',
      })}
    >
      <div className="flex h-full w-full items-center justify-center">
        <UntitledIcon className="h-4/5 w-4/5" name="user-03" />
      </div>
    </span>
  );
};

export default Avatar;
